/** The next sends of a schedule, in its time zone, with daylight-saving notes. */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { queryKeys } from "../services/queryKeys";
import { getApiErrorMessage } from "../services/apiErrors";
import { previewDistributionSchedule } from "../services/distributions/distributionsApi";
import type { DistributionScheduleApi, SchedulePreviewApi } from "../services/distributions/distributions.types";

export interface UseSchedulePreviewOptions {
  count?: number;
  runsDone?: number;
  /** Skip the request while the form has errors. */
  enabled?: boolean;
}

export interface UseSchedulePreviewResult {
  preview: SchedulePreviewApi | null;
  isLoading: boolean;
  errorMessage: string | null;
}

export function useSchedulePreview(
  storyId: string | undefined,
  schedule: DistributionScheduleApi | null,
  { count = 5, runsDone = 0, enabled = true }: UseSchedulePreviewOptions = {},
): UseSchedulePreviewResult {
  const body = schedule ? { schedule, count, runs_done: runsDone } : null;
  const query = useQuery({
    queryKey: queryKeys.distributions.schedulePreview(storyId ?? "", JSON.stringify(body)),
    queryFn: () => previewDistributionSchedule(storyId as string, body as NonNullable<typeof body>),
    enabled: Boolean(storyId && body && enabled),
    placeholderData: keepPreviousData,
    retry: false,
  });
  return {
    preview: body ? query.data ?? null : null,
    isLoading: query.isLoading,
    errorMessage: query.isError ? getApiErrorMessage(query.error, "Couldn’t work out when this sends.") : null,
  };
}
